import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { CallCenter } from '@app/core/interfaces/c5/acd/call-center';
import { Queue } from '@app/core/interfaces/c5/acd/queue';
import { Announcement } from '@app/core/interfaces/c5/announcement';
import { Device } from '@app/core/interfaces/c5/device';
import { DeviceType } from '@app/core/interfaces/c5/device-type.enum';
import { EndUser } from '@app/core/interfaces/c5/end-user';
import { Feature } from '@app/core/interfaces/c5/feature';
import { FeatureCode } from '@app/core/interfaces/c5/feature-code';
import { FeatureName } from '@app/core/interfaces/c5/feature-name';
import { PhoneNumberDirection } from '@app/core/interfaces/c5/phone-number-direction';
import { PhoneNumberType } from '@app/core/interfaces/c5/phone-number-type';
import { Role } from '@app/core/interfaces/c5/role';
import { RoomGroup } from '@app/core/interfaces/c5/room-group';
import { VoiceMail } from '@app/core/interfaces/c5/voice-mail';
import { CallbackNumber, CallbackNumbers } from '@app/core/interfaces/callback-numbers';
import { DeviceServiceType } from '@app/core/interfaces/device-service-type';
import { EndUserData } from '@app/core/interfaces/end-user-data';
import { EnterpriseOption } from '@app/core/interfaces/enterprise-option';
import {
  AuthUserFeatureAccess,
  FeatureProfile,
  FeatureProfileFeature,
  filterOptionalFeaturesBasedOnEnterpriseOptions,
} from '@app/core/interfaces/feature-profile';
import { IvrAssistant } from '@app/core/interfaces/ivr-assistant';
import { Site } from '@app/core/interfaces/site';
import { SecurityPermissionName } from '@app/core/models/permission';
import { FeatureRepository } from '@app/core/repositories/feature.repository';
import {
  ApplicationEventService,
  EndUserFeatureProfileChanged,
  FeatureChangedEvent,
  OptionalFeatureChangedEvent,
} from '@app/core/services/application-events';
import { TeamsTrunkAssignmentState } from '@app/shared/models/teams-trunk-assignment-state';
import { CachedHelperService } from '@app/shared/services/cached-helper.service';
import { ContextHelperService } from '@app/shared/services/context/context-helper.service';
import { EndUserContextService } from '@app/shared/services/context/end-user-context.service';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { TrunkUtilService } from '@app/shared/services/trunk-util.service';
import { TypeaheadServiceInput } from '@app/shared/services/typeahead-basket.service';
import { UploadInProgress } from '@app/shared/services/upload.service';
import { Cached, cachedGroup } from '@app/utils/cached';
import { groupBy } from '@app/utils/common';
import { deepcopy } from '@app/utils/deepmerge';
import { wrapError } from '@app/utils/rxjs';
import { sortBy } from '@app/utils/sort/sort-by';
import { StringMap } from '@app/utils/string-map';
import { extendUserWithExtension } from '@app/utils/user';
import { CallbackViewModel } from '@business/feature/components/features/callback/callback-view-model';
import {
  ExtendedFeatureProfile,
  ExtendedFeatureProfileFeature,
} from '@business/feature/interfaces/feature-profile';
import { FeatureStatus } from '@business/feature/interfaces/feature-status';
import { Features } from '@business/feature/interfaces/features';
import { FeatureDistributionSettings } from '@business/feature/models/feature-distribution-settings';
import { FeaturesViewModel } from '@business/feature/models/features-view-model';
import { MobileControlViewModel } from '@business/feature/models/mobile-control-view-model';
import { PhoneBookModel } from '@business/phone-book/models/phone-book-model';
import { forkJoin, Observable, of } from 'rxjs';
import { delayWhen, map, mergeMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FeatureService {
  private _callbackNumbers: Cached<CallbackViewModel> | null = null;

  get callbackNumbers(): Cached<CallbackViewModel> {
    this._callbackNumbers ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.featureRepository.getCallbackNumbers().pipe(
          map((numbers) => this.mapNumbersForCallback(numbers)),
          wrapError('feature-service:callback-numbers')
        ),
      id: ([userId]) => `user:${userId}:callback-numbers`,
      rememberTtl: 10_000,
      routeParameters: ['userId'],
    });

    return this._callbackNumbers;
  }

  private _extendedFeatureProfile: Cached<ExtendedFeatureProfile> | null = null;

  private get extendedFeatureProfile(): Cached<ExtendedFeatureProfile> {
    this._extendedFeatureProfile ??= cachedGroup(
      [
        this.endUserContextService.endUser,
        this.endUserContextService.activeFeatureProfile,
        this.enterpriseContextService.enterpriseOptionsSource,
        this.enterpriseContextService.enterprise,
        this.enterpriseContextService.screenNumbers,
      ],
      ([endUser, featureProfile, enterpriseOptions, enterprise, screenNumbers]) => {
        const enterpriseAllowsPreviews = enterprise.local?.allowPreviews;
        // TODO the check is taken from src/app/business/screen-number/effects/screen-number.effects.ts:283 (mapScreenNumber) where `ScreenNumberUsageType` is calculated.
        // this should be refactored into a general module from the business module
        const enterpriseHasServiceNumber = screenNumbers.some(
          (sn) => sn.dir === PhoneNumberDirection.OUT && sn.exactMatch === true
        );
        return this.getExtendedFeatureProfile(
          endUser.user,
          enterpriseOptions,
          featureProfile,
          enterpriseAllowsPreviews,
          enterpriseHasServiceNumber
        );
      },
      {
        id: 'feature-context:extended-feature-profile',
      }
    );

    return this._extendedFeatureProfile;
  }

  private _features: Cached<FeaturesViewModel> | null = null;

  get features(): Cached<FeaturesViewModel> {
    this._features ??= cachedGroup(
      [
        this.enterpriseContextService.enterpriseOptionsSource,
        this.enterpriseContextService.ivrAssistants,
        this.endUserContextService.announcements,
        this.endUserContextService.cstaDevices,
        this.endUserContextService.devices,
        this.endUserContextService.endUser,
        this.endUserContextService.features,
        this.endUserContextService.featureCodes,
        this.endUserContextService.rooms,
        this.contextHelperService.acdQueues,
        this.contextHelperService.callCenterQueues,
        this.contextHelperService.phoneBookContacts,
        this.extendedFeatureProfile,
      ],
      ([
        enterpriseOptions,
        ivrAssistants,
        announcements,
        cstaDevices,
        devices,
        endUserData,
        features,
        featureCodes,
        rooms,
        acdQueues,
        callCenter,
        phoneBook,
        extendedFeatureProfile,
      ]) =>
        this.listFeatures(
          callCenter,
          enterpriseOptions,
          endUserData,
          devices,
          cstaDevices,
          rooms,
          acdQueues,
          ivrAssistants,
          featureCodes,
          announcements,
          phoneBook,
          features,
          extendedFeatureProfile
        ),
      {
        id: 'feature-context:features',
      }
    );

    return this._features;
  }

  private _mobileControlNumbers: Cached<MobileControlViewModel> | null = null;

  get mobileControlNumbers(): Cached<MobileControlViewModel> {
    this._mobileControlNumbers ??= cachedGroup(
      [this.endUserContextService.endUser],
      ([endUser]) => {
        const site = endUser.domain as Site;

        const callThroughNumbers =
          site.serviceUsers
            .filter((user) => user.role === Role.MOBILE_CONTROL)
            .map((user) => user.phoneNumbers)
            .reduce((acc, number) => acc.concat(number), []) || [];

        const mobileNumbers = endUser.user.phoneNumbers.filter(
          (number) => number.type === PhoneNumberType.MSISDN
        );

        return of({
          callThroughNumbers,
          mobileNumbers,
        });
      },
      {
        id: `feature-context:mobile-control-numbers`,
      }
    );

    return this._mobileControlNumbers;
  }

  constructor(
    private applicationEventService: ApplicationEventService,
    private authenticationService: AuthenticationService,
    private cachedHelperService: CachedHelperService,
    private contextHelperService: ContextHelperService,
    private endUserContextService: EndUserContextService,
    private enterpriseContextService: EnterpriseContextService,
    private featureRepository: FeatureRepository,
    private trunkUtilService: TrunkUtilService
  ) {
    applicationEventService.on$(EndUserFeatureProfileChanged).subscribe(() => {
      this.extendedFeatureProfile.clear();
      this.features.clear();
    });
  }

  distributeFeatures(feature: Feature, userId: string): Observable<void> {
    return this.featureRepository.distribute(feature, userId).pipe(
      tap(() => {
        this._features?.reloadOrClear(false);
      })
    );
  }

  getFeatureForDistribution(featureId: number | string): Observable<FeatureDistributionSettings> {
    return this.featureRepository.get(featureId).pipe(
      mergeMap((feature) =>
        this.featureRepository.getPeersWithFeature(feature.featName).pipe(
          map((users): FeatureDistributionSettings => {
            return {
              feature,
              users: users.map((user) => extendUserWithExtension(user)),
            };
          })
        )
      ),
      wrapError('feature-service:get-feature-for-distribution')
    );
  }

  resetFeatures(): Observable<Features> {
    return this.featureRepository
      .resetFeatures()
      .pipe(delayWhen(() => this.endUserContextService.features.reload()));
  }

  toggleOptionalFeature(feature: FeatureProfileFeature, featureProfileId: number): Observable<void> {
    const updatedFeature: FeatureProfileFeature = {
      ...feature,
      userOptionalFeatureActive: !feature.userOptionalFeatureActive,
    };

    return this.featureRepository.updateOptionalFeature(updatedFeature, featureProfileId).pipe(
      delayWhen(() =>
        forkJoin([
          this.endUserContextService.features.reload(),
          this.endUserContextService.activeFeatureProfile.reload(),
        ])
      ),
      tap(() => {
        this.applicationEventService.emit(
          OptionalFeatureChangedEvent({
            featureName: feature.name,
          })
        );
      }),
      wrapError('feature-service:toggle-optional-feature')
    );
  }

  update(feature: Feature): Observable<void> {
    let returnValue$: Observable<any>;

    if (feature.id !== 0) {
      returnValue$ = this.featureRepository.update(feature);
    } else {
      returnValue$ = this.featureRepository.add(feature);
    }

    return returnValue$.pipe(
      delayWhen(() => this.endUserContextService.features.reload()),
      tap(() => {
        this.applicationEventService.emit(
          FeatureChangedEvent({
            featureName: feature.featName,
            userId: feature.userId,
          })
        );
      }),
      wrapError('feature-service:update')
    );
  }

  updateVoiceMail(
    file: File | null,
    feature: VoiceMail,
    removeFile: boolean
  ): Observable<UploadInProgress | void> {
    if (removeFile === true) {
      return this.featureRepository.removeVoiceMail().pipe(
        mergeMap(() => this.update(feature)),
        wrapError('feature-service:update-voice-mail')
      );
    } else if (feature.voiceMailData.personalAnnouncement === true) {
      if (file != null) {
        return this.featureRepository.updateVoiceMail(file).pipe(
          mergeMap((upload) => {
            if (upload.finished) {
              return this.update(feature);
            } else {
              return of(upload as UploadInProgress);
            }
          }),
          wrapError('feature-service:update-voice-mail')
        );
      }
    }

    return this.update(feature).pipe(wrapError('feature-service:update-voice-mail'));
  }

  private listFeatures(
    callCenter: Queue[],
    enterpriseOptions: EnterpriseOption[],
    endUserData: EndUserData,
    validDevices: Device[],
    cstaDevices: Device[],
    rooms: RoomGroup[],
    acdQueues: Queue[],
    ivrAssistants: IvrAssistant[],
    featureCodes: FeatureCode[],
    announcements: Announcement[],
    phoneBook: PhoneBookModel[],
    features: Features,
    extendedFeatureProfile: ExtendedFeatureProfile
  ): Observable<FeaturesViewModel> {
    const richPresenceActive =
      features.richPresence?.status === FeatureStatus.ACTIVE &&
      (endUserData.domain.richPresenceActivity ?? 1) > 0;

    const viewModel = this.mapResult(
      acdQueues,
      announcements,
      callCenter,
      cstaDevices,
      endUserData,
      enterpriseOptions,
      featureCodes,
      extendedFeatureProfile,
      features,
      ivrAssistants,
      phoneBook,
      richPresenceActive,
      rooms,
      validDevices
    );

    return of(viewModel);
  }

  private getExtendedFeatureProfile(
    endUser: EndUser,
    enterpriseOptions: EnterpriseOption[],
    activeFeatureProfile: FeatureProfile,
    enterpriseAllowsPreviews: boolean,
    enterpriseHasServiceNumber: boolean
  ): Observable<ExtendedFeatureProfile> {
    const featureProfile = {
      ...activeFeatureProfile,
      features: filterOptionalFeaturesBasedOnEnterpriseOptions(
        activeFeatureProfile.features ?? [],
        enterpriseOptions
      ),
    };

    let featureProfile$ = of(
      this.mapToExtendedFeatureProfile(featureProfile, enterpriseAllowsPreviews, enterpriseHasServiceNumber)
    );

    if (this.authenticationService.userIsEndUser() === false) {
      const featureNameToEnterpriseOptionName = {
        AD_HOC_CONFERENCING: 'AdHocConferenceFeatures',
        CALL_REC_PERMANENT: 'CallRecPermanentFeatures',
        CSTA_3RD_PARTY: 'CstaFeatures',
        DESKTOP_COMMUNICATOR: 'DesktopCommunicator',
        DESKTOP_CTI: 'DesktopCti',
        DESKTOP_OPERATOR: 'DesktopOperator',
        FAX_MAIL: 'FaxmailFeatures',
        MICROSOFT_TEAMS: 'MicrosoftTeamsUsers',
        MOBILE_COMMUNICATOR: 'MobileCommunicator',
        MOBILE_OFFICE: 'MobileOffice',
        UC_COLLABORATION: 'CollaborationFeatures',
        UC_INSTANT_MESSAGING: 'InstantMessagingFeatures',
        VOICE_MAIL: 'VoicemailFeatures',
      };

      featureProfile$ = featureProfile$.pipe(
        mergeMap((featureProfile) =>
          this.trunkUtilService.checkTeamsTrunkAssignment$(endUser, featureProfile).pipe(
            map((trunkAssignment) => {
              return {
                enterpriseOptions,
                featureProfile,
                trunkAssignment,
              };
            })
          )
        ),
        map(({ enterpriseOptions, featureProfile, trunkAssignment }) => {
          const countTypeOptions: StringMap<EnterpriseOption> = {};
          let hasPermanentCallRecording = false;
          let isCallRecordingEnabled = false;

          enterpriseOptions.forEach((enterpriseOption) => {
            if (enterpriseOption.definition.optionType === 'COUNT') {
              countTypeOptions[enterpriseOption.name] = enterpriseOption;
            }

            if (enterpriseOption.name === 'CallRecService' && enterpriseOption.active === true) {
              isCallRecordingEnabled = true;
            }

            if (
              enterpriseOption.name === 'CallRecPermanentFeatures' &&
              enterpriseOption.count > 0 &&
              enterpriseOption.active === true
            ) {
              hasPermanentCallRecording = true;
            }
          });

          featureProfile.nonOptionalFeatures.forEach((feature) => {
            const enterpriseOptionName: any = featureNameToEnterpriseOptionName[feature.name];
            const enterpriseOption = countTypeOptions[enterpriseOptionName];

            if (enterpriseOptionName != null) {
              feature.hidden = enterpriseOption == null || enterpriseOption.active === false;
            }
          });

          featureProfile.optionalFeatures.forEach((feature) => {
            const enterpriseOption = countTypeOptions[featureNameToEnterpriseOptionName[feature.name]];

            feature.statusToggleEnabled = !(
              enterpriseOption !== undefined &&
              enterpriseOption.used >= enterpriseOption.available &&
              feature.userOptionalFeatureActive === false
            );

            if (enterpriseOption != null) {
              feature.licensesAvailable = enterpriseOption.available - enterpriseOption.used > 0;
            }

            if (feature.name === 'CALL_REC_ADHOC' || feature.name === 'VOICE_RECORDING') {
              feature.hidden = !(isCallRecordingEnabled || feature.userOptionalFeatureActive === true);
            } else if (feature.name === 'CALL_REC_PERMANENT') {
              feature.hidden = !(
                (isCallRecordingEnabled && hasPermanentCallRecording) ||
                feature.userOptionalFeatureActive === true
              );
            } else if (feature.name === 'MICROSOFT_TEAMS') {
              feature.hidden = enterpriseOption == null || enterpriseOption.active === false;

              if (
                trunkAssignment != null &&
                trunkAssignment != TeamsTrunkAssignmentState.AssignedToTeamsTrunk
              ) {
                feature.warning = `feature.MICROSOFT_TEAMS.warning`;
              }
            } else if (feature.name === 'UC_COLLABORATION') {
              feature.hidden = enterpriseOption == null || enterpriseOption.active === false;
            }
          });

          return featureProfile;
        })
      );
    }

    return featureProfile$;
  }

  private mapToExtendedFeatureProfile(
    featureProfile: FeatureProfile,
    enterpriseAllowsPreviews: boolean,
    enterpriseHasServiceNumber: boolean
  ): ExtendedFeatureProfile {
    const nonOptionalFeatures: ExtendedFeatureProfileFeature[] = [];
    const optionalFeatures: ExtendedFeatureProfileFeature[] = [];

    const canManageOptionalFeatures = this.authenticationService.hasPermission(
      SecurityPermissionName.MANAGE_USER_OPTIONAL_FEATURES
    );

    for (let feature of featureProfile.features) {
      const extendedFeature: ExtendedFeatureProfileFeature = {
        ...deepcopy(feature),
        enabled: false,
        hidden: false,
        statusToggleEnabled: false,
      };

      // should a feature be visible in the view based on auth-user access setting
      if (this.authenticationService.userIsServiceProviderAdmin()) {
        extendedFeature.hidden = feature.resellerAccess === AuthUserFeatureAccess.NONE;
      } else if (
        this.authenticationService.userIsEnterpriseAdmin() ||
        this.authenticationService.userIsEndUser()
      ) {
        // also hide the optional-feature for end-user if it is hidden for an enterprise admin
        extendedFeature.hidden = feature.enterpriseAdminAccess === AuthUserFeatureAccess.NONE;
      }

      // should an optional feature be enabled in the view based on auth-user access setting
      if (feature.optional === true) {
        if (canManageOptionalFeatures) {
          if (this.authenticationService.userIsServiceProviderAdmin()) {
            extendedFeature.enabled = feature.resellerAccess === AuthUserFeatureAccess.MANAGE;
            extendedFeature.hidden = feature.resellerAccess === AuthUserFeatureAccess.NONE;
          } else if (this.authenticationService.userIsEnterpriseAdmin()) {
            extendedFeature.enabled = feature.enterpriseAdminAccess === AuthUserFeatureAccess.MANAGE;
            extendedFeature.hidden = feature.enterpriseAdminAccess === AuthUserFeatureAccess.NONE;
          } else if (this.authenticationService.userIsEndUser()) {
            extendedFeature.enabled = false;
          } else {
            extendedFeature.enabled = true;
          }
        } else {
          extendedFeature.enabled = false;
        }

        // hide feature if it is optional and deprecated and not active
        if (feature.deprecated === true && feature.userOptionalFeatureActive === false) {
          extendedFeature.hidden = true;
        }
        // hide feature if it is optional and preview and enterprise does not allow previews and not active
        if (
          feature.preview === true &&
          enterpriseAllowsPreviews === false &&
          feature.userOptionalFeatureActive === false
        ) {
          extendedFeature.hidden = true;
        }
        // hide feature SERVICE_NUMBER if the enterprise has no service number configured (#65983)
        if (
          feature.name === FeatureName.SERVICE_NUMBER &&
          enterpriseHasServiceNumber === false &&
          feature.userOptionalFeatureActive === false
        ) {
          extendedFeature.hidden = true;
        }

        optionalFeatures.push(extendedFeature);
      } else {
        nonOptionalFeatures.push(extendedFeature);
      }
    }

    nonOptionalFeatures.sort(sortBy('name'));
    optionalFeatures.sort(sortBy('name'));

    return {
      ...featureProfile,
      nonOptionalFeatures,
      optionalFeatures,
    };
  }

  private mapNumbersForCallback(callbackNumbers: CallbackNumbers): CallbackViewModel {
    const get = (numbers: CallbackNumber[], role: string): TypeaheadServiceInput[] => {
      const numbersByRole = numbers.filter((number) => number.user?.role === role);
      const groupedByName = groupBy(numbersByRole, (number) => number.user?.lastName);

      return Object.entries(groupedByName).map(([name, numbers]) => {
        return {
          name,
          phoneNumbers: numbers.filter((number) => number.internal != null),
        };
      });
    };

    const vm: CallbackViewModel = {
      externalNumbers: {
        acdQueues: get(callbackNumbers.external, Role.ACD_QUEUE),
        callCenter: get(callbackNumbers.external, Role.CALL_CENTER),
        ivrAssistants: get(callbackNumbers.external, Role.IVR_ASSISTANT),
        numbers: callbackNumbers.external.filter((entry) => entry.user?.role == Role.END_USER),
        screenNumbers: [],
      },
      internalNumbers: {
        acdQueues: get(callbackNumbers.internal, Role.ACD_QUEUE),
        callCenter: get(callbackNumbers.internal, Role.CALL_CENTER),
        ivrAssistants: get(callbackNumbers.internal, Role.IVR_ASSISTANT),
        numbers: callbackNumbers.internal.filter((number) => number.user?.role == Role.END_USER),
      },
    };

    vm.externalNumbers.screenNumbers = callbackNumbers.screenNumbers.filter(
      (screenNumber) =>
        screenNumber.user != null ||
        callbackNumbers.external.some((number) => number.e164 === screenNumber.e164)
    );

    return vm;
  }

  private mapResult(
    acdQueues: Queue[],
    announcements: Announcement[],
    callCenter: CallCenter | Queue[],
    cstaDevices: Device[],
    endUserData: EndUserData,
    enterpriseOptions: EnterpriseOption[],
    featureCodes: FeatureCode[],
    featureProfile: ExtendedFeatureProfile,
    features: Features,
    ivrAssistants: IvrAssistant[],
    phoneBook: PhoneBookModel[],
    richPresenceActive: boolean,
    rooms: RoomGroup[],
    validDevices: Device[]
  ): FeaturesViewModel {
    featureProfile.optionalFeatures.forEach((optionalFeature) => {
      if (optionalFeature.blockedBy != null && optionalFeature.statusToggleEnabled) {
        const maybeBlockingFeature = featureProfile.optionalFeatures.find(
          (feature) => feature.name === optionalFeature.blockedBy
        );

        if (maybeBlockingFeature?.userOptionalFeatureActive) {
          optionalFeature.statusToggleEnabled = false;
          optionalFeature.disabledReason = 'blockedByFeature';
        }
      }
    });

    if (features.csta != null) {
      cstaDevices = cstaDevices.filter((device) => {
        return (
          features.csta.cstaData.devId === device.id ||
          (device.type === DeviceType.PHONE &&
            (device.serviceType === DeviceServiceType.UNKNOWN ||
              device.serviceType === DeviceServiceType.MOBILE_CONTROL ||
              device.serviceType === DeviceServiceType.MOBILE_COMMUNICATOR_WITHOUT_GSM ||
              device.serviceType === DeviceServiceType.DESKTOP_CONTROL ||
              device.serviceType === DeviceServiceType.DESKTOP_OPERATOR ||
              device.serviceType === DeviceServiceType.WEB_CONTROL))
        );
      });
    }

    return {
      acdQueues,
      announcements,
      callCenter,
      validDevices,
      cstaDevices,
      endUser: endUserData,
      featureCodes,
      featureProfile,
      features,
      ivrAssistants,
      enterpriseOptions,
      phoneBook,
      richPresenceActive,
      rooms,
      serviceUsers: endUserData.domain?.serviceUsers ?? [],
    };
  }
}
